/**
 * useLocalStorage
 *
 * A reactive wrapper around localStorage that uses lsGetItem / lsSetItem for
 * typed, serialized access. State is kept in sync across all hook instances
 * that share the same key — both within the same tab (via a custom DOM event)
 * and across tabs/windows (via the native `storage` event).
 *
 * Two call signatures are supported:
 *
 *   1. Full-object — reads and writes the entire value at a key:
 *        const [theme, setTheme] = useLocalStorage(LocalStorageKey.THEME, 'light')
 *
 *   2. Dot-path — reads and writes a single nested field, leaving the rest of
 *      the stored object untouched. The path is type-safe and autocompleted:
 *        const [done, setDone] = useLocalStorage(
 *          LocalStorageKey.COMPLETED_TOURS, 'full-page-onboarding', false
 *        )
 *        // deeper paths also work:
 *        const [ts, setTs] = useLocalStorage(
 *          LocalStorageKey.NEWS_TICKER_DISMISSED, 'user-123.news-456', 0
 *        )
 */
import { useCallback, useEffect, useState } from 'react'

import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { LocalStorageValue } from 'shared/types/localStorage'
import {
  dispatchCustomEvent,
  listenCustomEvent
} from 'shared/utils/customEvents'
import { lsGetItem, lsSetItem } from 'shared/utils/local-storage'

// ---------------------------------------------------------------------------
// Dot-path types
// ---------------------------------------------------------------------------

type Paths<T> = T extends object
  ? {
      [K in keyof T & string]: K | `${K}.${Paths<NonNullable<T[K]>>}`
    }[keyof T & string]
  : never

type PathValue<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? PathValue<NonNullable<T[K]>, Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never

type ObjectValue<K extends LocalStorageKey> = NonNullable<LocalStorageValue[K]>

type SetValue<T> = T | ((prev: T) => T)

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

const getAtPath = (obj: unknown, path: string): unknown => {
  return path.split('.').reduce((acc, segment) => {
    if (acc == null || typeof acc !== 'object') return undefined
    return (acc as Record<string, unknown>)[segment]
  }, obj)
}

const setAtPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> => {
  const [head, ...rest] = path.split('.')
  if (rest.length === 0) return { ...obj, [head]: value }
  return {
    ...obj,
    [head]: setAtPath(
      (obj[head] ?? {}) as Record<string, unknown>,
      rest.join('.'),
      value
    )
  }
}

// ---------------------------------------------------------------------------
// Overloads
// ---------------------------------------------------------------------------

// Full-object overload
export function useLocalStorage<K extends LocalStorageKey>(
  key: K,
  fallback: LocalStorageValue[K]
): [LocalStorageValue[K], (next: SetValue<LocalStorageValue[K]>) => void]

// Dot-path overload — reads/writes only the value at the given path
export function useLocalStorage<
  K extends LocalStorageKey,
  P extends Paths<ObjectValue<K>>
>(
  key: K,
  path: P,
  fallback: PathValue<ObjectValue<K>, P>
): [
  PathValue<ObjectValue<K>, P>,
  (next: SetValue<PathValue<ObjectValue<K>, P>>) => void
]

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function useLocalStorage<K extends LocalStorageKey>(
  key: K,
  pathOrFallback: string | LocalStorageValue[K],
  fallback?: unknown
) {
  const hasPath = fallback !== undefined

  const readValue = useCallback(() => {
    const stored = lsGetItem(key)
    if (hasPath) {
      return getAtPath(stored, pathOrFallback as string) ?? fallback
    }
    return stored ?? pathOrFallback
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [value, setValueState] = useState(readValue)

  const setValue = useCallback(
    (next: SetValue<unknown>) => {
      setValueState(prev => {
        const resolved = typeof next === 'function' ? next(prev) : next

        if (hasPath) {
          const current = (lsGetItem(key) ?? {}) as Record<string, unknown>
          lsSetItem(
            key,
            setAtPath(
              current,
              pathOrFallback as string,
              resolved
            ) as LocalStorageValue[K]
          )
        } else {
          lsSetItem(key, resolved as LocalStorageValue[K])
        }

        dispatchCustomEvent(CustomEvents.LOCAL_STORAGE_CHANGED, { key })
        return resolved
      })
    },
    [key] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    const syncFromStorage = () => {
      const next = readValue()
      if (next !== null) setValueState(next)
    }

    const unlistenSameTab = listenCustomEvent(
      CustomEvents.LOCAL_STORAGE_CHANGED,
      ({ key: changedKey }) => {
        if (changedKey === key) syncFromStorage()
      }
    )

    const onStorageEvent = (e: StorageEvent) => {
      if (e.key === key) syncFromStorage()
    }
    window.addEventListener('storage', onStorageEvent)

    return () => {
      unlistenSameTab()
      window.removeEventListener('storage', onStorageEvent)
    }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setValue]
}
